import { SizeEnum } from "@booking/enums";
import { usePublish } from "@booking/hooks/usePubSub";
import { getEnumKeyByEnumValue } from "@booking/utils/helpers";
import clsx from "clsx";
import React from "react";

import Icon from "@/components/atoms/media/Icon";
import type { CommonProps, InputElementProps } from "@/types";
import { createId } from "@/utils/helpers";

import styles from "./TextInput.module.css";

const ref = "text-input";

enum TextInputAutocompleteEnum {
  DEFAULT = "",
  ADDITIONAL_NAME = "additional-name",
  ADDRESS_LINE1 = "address-line1",
  ADDRESS_LINE2 = "address-line2",
  ADDRESS_LINE3 = "address-line3",
  ADDRESS_LEVEL1 = "address-level1",
  ADDRESS_LEVEL2 = "address-level2",
  ADDRESS_LEVEL3 = "address-level3",
  ADDRESS_LEVEL4 = "address-level4",
  BDAY = "bday",
  BDAY_DAY = "bday-day",
  BDAY_MONTH = "bday-month",
  BDAY_YEAR = "bday-year",
  COUNTRY = "country",
  COUNTRY_NAME = "country-name",
  CURRENT_PASSWORD = "current-password",
  EMAIL = "email",
  FAMILY_NAME = "family-name",
  GIVEN_NAME = "given-name",
  HONORIFIC_PREFIX = "honorific-prefix",
  HONORIFIC_SUFFIX = "honorific-suffix",
  NAME = "name",
  NEW_PASSWORD = "new-password",
  NICKNAME = "nickname",
  OFF = "off",
  ON = "on",
  ONE_TIME_CODE = "one-time-code",
  ORGANIZATION = "organization",
  ORGANIZATION_TITLE = "organization-title",
  POSTAL_CODE = "postal-code",
  STREET_ADDRESS = "street-address",
  TEL = "tel",
  TEL_AREA_CODE = "tel-area-code",
  TEL_COUNTRY_CODE = "tel-country-code",
  TEL_EXTENSION = "tel-extension",
  TEL_LOCAL = "tel-local",
  TEL_NATIONAL = "tel-national",
  URL = "url",
  USERNAME = "username",
}

enum TextInputModeEnum {
  DECIMAL = "decimal",
  EMAIL = "email",
  NUMERIC = "numeric",
  SEARCH = "search",
  TEL = "tel",
  TEXT = "text",
  URL = "url",
}

enum TextInputEventEnum {
  BLUR = "blur",
  CHANGE = "change",
  FOCUS = "focus",
  INPUT = "input",
}

enum TextInputTypeEnum {
  EMAIL = "email",
  PASSWORD = "password",
  SEARCH = "search",
  TEL = "tel",
  URL = "url",
}

interface TextInputProps extends Partial<CommonProps & InputElementProps> {
  inputMode?: TextInputModeEnum;
  list?: Array<string>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onInput?: React.FormEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onUpdate?: (value: any) => void;
  ref?: React.Ref<HTMLInputElement>;
  spellcheck?: boolean;
  type?: TextInputTypeEnum;
}

function useTextInput(props: TextInputProps) {
  const { color, list = [], maxLength = 0, minLength = 0, type } = props;
  const className = clsx(
    styles[ref],
    color && styles[color],
    props.invalid && styles.invalid,
    props.icon && styles.icon,
    props.noLabel && styles["no-label"],
    props.className,
  );
  const id = props.id || createId(ref);
  const inputMode =
    props.inputMode || type
      ? getEnumKeyByEnumValue(TextInputModeEnum, type as string)
      : undefined;

  return {
    children: props.children,
    className,
    id,
    inputMode: inputMode ? TextInputModeEnum[inputMode] : undefined,
    label: props.label,
    list,
    listId: list.length > 0 ? `${id}_list` : undefined,
    maxLength: maxLength !== 0 ? maxLength : undefined,
    minLength: minLength !== 0 ? minLength : undefined,
    type,
  };
}

function TextInput(props: TextInputProps) {
  const {
    children,
    className,
    id,
    inputMode,
    label,
    list,
    listId,
    maxLength,
    minLength,
    type,
  } = useTextInput(props);
  const { icon, iconColor, name = id, value } = props;

  const publish = usePublish({ context: name, ...props.pubSubData });

  function onchange(e: React.ChangeEvent<HTMLInputElement>) {
    publish(TextInputEventEnum.CHANGE, { value: e.target.value });

    props.onChange && props.onChange(e);
  }

  return (
    <label className={className} htmlFor={id}>
      {(label || children) && (
        <span className={props.labelClassName}>
          {label}

          {children}
        </span>
      )}

      <input
        ref={props.ref}
        autoComplete={props.autocomplete}
        autoFocus={props.autofocus}
        defaultValue={value || undefined}
        disabled={!!props.disabled}
        id={id}
        inputMode={inputMode}
        list={listId}
        maxLength={maxLength}
        minLength={minLength}
        name={name}
        onBlur={props.onBlur}
        onChange={onchange}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        onInput={props.onInput}
        placeholder={props.placeholder}
        readOnly={props.readOnly}
        required={props.required}
        role={props.role}
        size={props.size}
        spellCheck={props.spellcheck}
        type={type}
      />

      {list.length > 0 && (
        <datalist id={listId}>
          {list.map((item, index) => (
            <option key={`${listId}_${index}`}>{item}</option>
          ))}
        </datalist>
      )}

      {icon && <Icon color={iconColor} name={icon} size={SizeEnum.LARGE} />}
    </label>
  );
}

export default TextInput;
export {
  TextInput,
  type TextInputProps,
  TextInputAutocompleteEnum,
  TextInputEventEnum,
  TextInputModeEnum,
  TextInputTypeEnum,
};
